/* The public account ID matches the store's existing Flashy installation.
 * This is the same reviews endpoint and aggregates used by Flashy's product widget.
 * The SDK's reviews.get() omits stats, so request the public response directly.
 */
(() => {
  if (customElements.get('wholedent-review-rating')) return;

  const accountId = document.currentScript?.dataset.flashyAccountId;
  const validId = (value) => typeof value === 'string' && /^[1-9]\d*$/.test(value);
  const number = (value) => {
    if (typeof value !== 'number' && (typeof value !== 'string' || !value.trim())) return NaN;
    return Number(value);
  };
  const cache = new Map();
  const queue = [];
  let active = 0;

  async function fetchRating(productId) {
    const controller = new AbortController();
    let timer;
    try {
      const url = new URL('https://api.flashy.app/thunder/reviews');
      url.searchParams.set('account_id', accountId);
      url.searchParams.set('item_id', productId);
      url.searchParams.set('limit', '1');
      url.searchParams.set('page', '1');
      const expired = new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Review rating request timed out'));
        }, 12000);
      });
      const result = await Promise.race([
        (async () => {
          const response = await fetch(url.toString(), { credentials: 'omit', signal: controller.signal });
          if (!response.ok) return null;
          return response.json();
        })(),
        expired,
      ]);
      if (result?.success !== true || String(result.data?.product?.id) !== productId) return null;
      const total = number(result.data.total);
      const average = number(result.data.stats?.avg);
      // total is all reviews; count/results.length only describes the requested page.
      if (!Number.isSafeInteger(total) || total < 0) return null;
      if (total === 0) return { total: 0, average: 0 };
      if (!Number.isFinite(average) || average < 1 || average > 5) return null;
      return { total, average };
    } catch (_) {
      // A missing rating is preferable to showing an unverified saved value.
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  function drain() {
    while (active < 2 && queue.length) {
      const { productId, resolve } = queue.shift();
      active += 1;
      fetchRating(productId).then(resolve).finally(() => {
        active -= 1;
        drain();
      });
    }
  }

  function getRating(productId) {
    if (!validId(accountId) || !validId(productId)) return Promise.resolve(null);
    if (!cache.has(productId)) {
      cache.set(productId, new Promise((resolve) => queue.push({ productId, resolve })));
      drain();
    }
    return cache.get(productId);
  }

  const observedCards = new Map();
  const observer = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        const ratings = [...(observedCards.get(target) || [])];
        ratings.forEach((rating) => {
          stopObserving(rating);
          rating.loadRating();
        });
      });
    }, { rootMargin: '200px' })
    : null;

  function stopObserving(rating) {
    const target = rating.observationTarget;
    const ratings = observedCards.get(target);
    ratings?.delete(rating);
    if (ratings && !ratings.size) {
      observer.unobserve(target);
      observedCards.delete(target);
    }
    rating.observationTarget = null;
  }

  function observeCard(rating) {
    // The rating itself is display:none until verified and cannot intersect.
    const target = rating.closest('.card') || rating.parentElement;
    if (!target) return;
    if (!observedCards.has(target)) {
      observedCards.set(target, new Set());
      observer.observe(target);
    }
    observedCards.get(target).add(rating);
    rating.observationTarget = target;
  }

  class WholeDentReviewRating extends HTMLElement {
    static get observedAttributes() { return ['data-product-id']; }

    connectedCallback() {
      this.version = (this.version || 0) + 1;
      this.hidden = true;
      this.dataset.ratingState = 'pending';
      stopObserving(this);
      if (this.hasAttribute('data-priority') || !observer) this.loadRating();
      else observeCard(this);
    }

    disconnectedCallback() {
      this.version = (this.version || 0) + 1;
      stopObserving(this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue && this.isConnected) this.connectedCallback();
    }

    async loadRating() {
      const version = this.version;
      const productId = this.dataset.productId;
      this.dataset.ratingState = 'loading';
      const summary = await getRating(productId);
      if (!this.isConnected || version !== this.version || this.dataset.productId !== productId) return;
      if (!summary || !summary.total) {
        this.dataset.ratingState = summary ? 'empty' : 'unavailable';
        return;
      }
      const rating = this.querySelector('.rating');
      const stars = this.querySelector('.rating__stars');
      const count = this.querySelector('.rating__count');
      if (!rating || !stars || !count) return;
      rating.setAttribute('aria-label', (this.dataset.ratingLabel || '[rating] out of 5 stars')
        .replace('[rating]', summary.average.toFixed(1)));
      stars.style.setProperty('--flashy-rating-percent', `${summary.average * 20}%`);
      count.textContent = `(${summary.total})`;
      this.dataset.ratingState = 'ready';
      this.hidden = false;
    }
  }

  // Custom-element lifecycle callbacks also cover filtered/inserted cards,
  // recommendations, sliders, and Shopify theme-editor section reloads.
  customElements.define('wholedent-review-rating', WholeDentReviewRating);
})();
